import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAgentChat, type ChatMessage } from './useAgentChat';
import type { ConfirmationPayload, DisplayMessage } from '../services/agentApi';

// The webapp UI is English only; switch to the i18n language once translations ship.
const UI_LANGUAGE = 'en';

export interface AgentContext {
    current_page: string;
    // Fallback language for the agent's reply when the user's message is ambiguous.
    ui_language: string;
    model_id?: number;
    model_name?: string;
    scenario_id?: number;
    scenario_name?: string;
    sensitivity_id?: number;
    sensitivity_name?: string;
}

interface ModelInfo {
    model_id: number;
    model_name: string;
    scenario_id?: number;
    scenario_name?: string;
    sensitivity_id?: number;
    sensitivity_name?: string;
}

interface AgentContextProviderValue {
    modelInfo: ModelInfo | null;
    setModelInfo: (info: ModelInfo | null) => void;
    isChatOpen: boolean;
    setIsChatOpen: (open: boolean) => void;
}

const AgentCtx = createContext<AgentContextProviderValue>({
    modelInfo: null,
    setModelInfo: () => {},
    isChatOpen: false,
    setIsChatOpen: () => {},
});

export function AgentContextProvider({ children }: { children: React.ReactNode }) {
    const [modelInfo, setModelInfoState] = useState<ModelInfo | null>(null);
    const [isChatOpen, setIsChatOpenState] = useState(false);

    const setModelInfo = useCallback((info: ModelInfo | null) => {
        setModelInfoState(info);
    }, []);

    const setIsChatOpen = useCallback((open: boolean) => {
        setIsChatOpenState(open);
    }, []);

    const value = useMemo(() => ({ modelInfo, setModelInfo, isChatOpen, setIsChatOpen }), [modelInfo, setModelInfo, isChatOpen, setIsChatOpen]);

    return React.createElement(AgentCtx.Provider, { value }, children);
}

export function useChatOpen() {
    const { isChatOpen, setIsChatOpen } = useContext(AgentCtx);
    return { isChatOpen, setIsChatOpen };
}

export function useSetAgentModelInfo() {
    return useContext(AgentCtx).setModelInfo;
}

function getPageLabel(pathname: string): string {
    if (pathname.includes('/models/editor/financial')) return 'financial-model-editor';
    if (pathname.includes('/models/editor/operational')) return 'operational-model-editor';
    if (pathname.includes('/models/new')) return 'new-model';
    if (pathname.includes('/models')) return 'my-models';
    if (pathname.includes('/financial-statements')) return 'financial-statements';
    if (pathname.includes('/dashboard')) return 'dashboard';
    if (pathname.includes('/audit')) return 'audit';
    if (pathname.includes('/history')) return 'history';
    if (pathname.includes('/tools')) return 'tools';
    if (pathname.includes('/compare')) return 'compare';
    if (pathname.includes('/calculation-results')) return 'calculation-results';
    if (pathname.includes('/library')) return 'library';
    if (pathname.includes('/organization')) return 'organization';
    if (pathname.includes('/settings')) return 'settings';
    if (pathname.includes('/account')) return 'account';
    if (pathname.includes('/contact')) return 'contact';
    if (pathname.includes('/ai')) return 'ai';
    return 'unknown';
}

export function useAgentContext(): AgentContext {
    const location = useLocation();
    const { modelInfo } = useContext(AgentCtx);

    return useMemo(() => {
        const ctx: AgentContext = {
            current_page: getPageLabel(location.pathname),
            ui_language: UI_LANGUAGE,
        };

        if (modelInfo) {
            ctx.model_id = modelInfo.model_id;
            ctx.model_name = modelInfo.model_name;
            if (modelInfo.scenario_id) {
                ctx.scenario_id = modelInfo.scenario_id;
                ctx.scenario_name = modelInfo.scenario_name;
            }
            if (modelInfo.sensitivity_id) {
                ctx.sensitivity_id = modelInfo.sensitivity_id;
                ctx.sensitivity_name = modelInfo.sensitivity_name;
            }
        }

        return ctx;
    }, [location.pathname, modelInfo]);
}

// --- Shared chat state (single conversation shared between side panel & AI page) ---

interface SharedChatState {
    messages: ChatMessage[];
    isLoading: boolean;
    isThinking: boolean;
    isAvailable: boolean | null;
    conversationId: string | null;
    autoConfirm: boolean;
    setAutoConfirm: (val: boolean) => void;
    sendMessage: (content: string) => void;
    handleConfirmation: (toolCallId: string, confirmed: boolean) => void;
    stopGeneration: () => void;
    clearChat: () => void;
    loadConversation: (id: string, messages: DisplayMessage[]) => void;
}

const SharedChatCtx = createContext<SharedChatState | null>(null);

export function SharedChatProvider({ children }: { children: React.ReactNode }) {
    const agentContext = useAgentContext();
    const chatState = useAgentChat(agentContext);

    return React.createElement(SharedChatCtx.Provider, { value: chatState }, children);
}

export function useSharedChat(): SharedChatState {
    const ctx = useContext(SharedChatCtx);
    if (!ctx) throw new Error('useSharedChat must be used within SharedChatProvider');
    return ctx;
}

export function useIsOnAIPage(): boolean {
    const location = useLocation();
    return location.pathname.includes('/ai');
}
